import {isTestEnv} from 'common/report/util/env';

// Frigade's assistant ships as a CDN script plus a `window.FrigadeAssistant`
// global, not an npm package. Before the script loads, the global is a stub:
// calling it queues configs on `.q`, and `.on()` queues subscriptions on
// `.on.q`. The imperative methods (`show`, `hide`, `close`, `getMode`, `ask`)
// are attached only once the script has loaded and are NOT queued, so every
// call site must check that the method exists before calling it.
// Docs: https://docs.frigade.com
export type FrigadeAssistantMode =
  | `hidden`
  | `collapsed`
  | `input`
  | `thinking`
  | `streaming-result`
  | `result`
  | `reply-input`
  | `tool-ui`
  | `error`;

interface FrigadeAssistantConfig {
  // The vendor's field is `apiKey`; `publicKey` is our name for the same value
  // (that is what `user_support_info` calls it). Sending `publicKey` makes the
  // vendor throw "Please provide a Frigade public API key" and abort its init.
  apiKey: string;
  // Required: the vendor ships no default and throws without it.
  apiUrl: string;
  assistantId: string;
  userId: string;
  userProperties?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  tools?: Record<string, unknown>;
  defaultVisible?: boolean;
}

type FrigadeEventHandler = (...args: unknown[]) => void;

type FrigadeOnFn = ((event: string, handler: FrigadeEventHandler) => void) & {
  q?: Array<{event: string; handler: FrigadeEventHandler}>;
};

type FrigadeAssistantApi = ((config: FrigadeAssistantConfig) => void) & {
  q?: FrigadeAssistantConfig[];
  on?: FrigadeOnFn;
  show?: () => void;
  hide?: () => void;
  close?: () => void;
  getMode?: () => FrigadeAssistantMode;
  ask?: (question: string) => void;
};

declare global {
  interface Window {
    FrigadeAssistant?: FrigadeAssistantApi;
  }
}

const FRIGADE_SCRIPT_SRC = `https://app.frigade.com/cdn/frigade-assistant.min.js`;
// Paired with the `api.frigade.ai` connect-src entries in
// kube/lib/webapp/nginx/csp-config.libsonnet - changing one needs the other.
const FRIGADE_API_URL = `https://api.frigade.ai`;

export interface LoadFrigadeAssistantOptions {
  publicKey: string;
  assistantId: string;
  // Required: the vendor throws "Please provide a userId to initialize Frigade"
  // without it, and it keys the conversation on their side.
  userId: string;
  userProperties?: Record<string, unknown>;
  preferences?: Record<string, unknown>;
  tools?: Record<string, unknown>;
}

type FrigadeShowFn = () => void;

interface FrigadeMountResult {
  // Whether the root for the config that produced this result attached its
  // methods before the deadline.
  ready: boolean;
  // The `show` closure that root installed. The next config sent for the same
  // identity mounts a root that replaces exactly this closure, so it is the
  // reference the next wait compares against.
  show: FrigadeShowFn | undefined;
}

interface FrigadeIdentityLoad {
  mounted: Promise<FrigadeMountResult>;
  // The caller-facing view of `mounted`. Held so that a repeat call the vendor
  // will satisfy with the same mount can hand back the identical promise.
  ready: Promise<boolean>;
}

// Keyed by publicKey/assistantId/userId so a credential or identity change
// between activations doesn't return the stale promise. Every entry chains off
// the one script promise; the key also carries the mount each identity is
// currently settled on, so the next config for it knows what to wait past.
const _frigadeLoads = new Map<string, FrigadeIdentityLoad>();

function identityCacheKey(publicKey: string, assistantId: string, userId: string): string {
  return `${publicKey}:${assistantId}:${userId}`;
}

// Kick off the script download with a bare identity, without showing the
// assistant. Safe to call repeatedly: once any config has been sent for this
// identity - this bare one or a full one carrying `userProperties` - later
// prewarms are no-ops, so they can never overwrite the properties with nothing.
export function prewarmFrigadeAssistant(publicKey: string, assistantId: string, userId: string): void {
  if (!publicKey || !assistantId || !userId) {
    return;
  }
  if (_frigadeLoads.has(identityCacheKey(publicKey, assistantId, userId))) {
    return;
  }
  void loadFrigadeAssistant({publicKey, assistantId, userId});
}

export function showFrigadeAssistant(): void {
  window.FrigadeAssistant?.show?.();
}

// Frigade drops an in-flight response when hidden mid-answer rather than
// saving it - callers should consult `getFrigadeMode()` before hiding while
// the assistant is thinking/streaming.
export function hideFrigadeAssistant(): void {
  window.FrigadeAssistant?.hide?.();
}

/**
 * @lintignore (knip) TODO(GRO-252): temporarily unreferenced — the visibility lifecycle work discards the conversation when the assistant is dismissed
 */
export function closeFrigadeConversation(): void {
  window.FrigadeAssistant?.close?.();
}

/**
 * @lintignore (knip) TODO(GRO-252): temporarily unreferenced — the visibility lifecycle work consults the mode before hiding mid-answer
 */
export function getFrigadeMode(): FrigadeAssistantMode | undefined {
  return window.FrigadeAssistant?.getMode?.();
}

/**
 * `ask()` works on a hidden assistant and brings it back on screen by itself.
 *
 * @lintignore (knip) TODO(GRO-250): temporarily unreferenced — consumed by the entry points that seed the assistant with a question
 */
export function askFrigadeAssistant(question: string): void {
  window.FrigadeAssistant?.ask?.(question);
}

// Frigade's `on()` does not dedupe handlers, so register once per page.
let _listenersRegistered = false;

/**
 * Wires event subscriptions through the queue-able `on()` stub, so it is safe
 * to call before the script has loaded. No-ops until the bootstrap has
 * installed the stub, and on every call after the first successful one.
 *
 * @lintignore (knip) TODO(GRO-254): temporarily unreferenced — the conversation-tracking work subscribes to the assistant's events
 */
export function registerFrigadeEventHandlers(handlers: Record<string, FrigadeEventHandler>): void {
  if (_listenersRegistered) {
    return;
  }
  const on = window.FrigadeAssistant?.on;
  if (!on) {
    return;
  }
  for (const [event, handler] of Object.entries(handlers)) {
    on(event, handler);
  }
  _listenersRegistered = true;
}

function installFrigadeBootstrap(): HTMLScriptElement {
  if (!window.FrigadeAssistant) {
    const stub = function (config: FrigadeAssistantConfig) {
      (stub.q = stub.q ?? []).push(config);
    } as FrigadeAssistantApi;
    window.FrigadeAssistant = stub;
  }
  if (!window.FrigadeAssistant.on) {
    const on = function (event: string, handler: FrigadeEventHandler) {
      (on.q = on.q ?? []).push({event, handler});
    } as FrigadeOnFn;
    window.FrigadeAssistant.on = on;
  }

  const existing = document.head.querySelector<HTMLScriptElement>(`script[src='${FRIGADE_SCRIPT_SRC}']`);
  if (existing && existing.dataset.frigadeTimedOut !== `true`) {
    return existing;
  }
  if (existing) {
    // The element outlived a 10s timeout without loading. Reaching here means
    // a caller is asking again after that grace period, so trade the stalled
    // element for a fresh download rather than attaching listeners to one that
    // may never fire again. An element that loaded late clears the marker in
    // its `load` handler and so is reused above.
    existing.remove();
  }
  const script = document.createElement(`script`);
  script.src = FRIGADE_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
  return script;
}

// The single owner of the script element: one promise per download attempt,
// no matter how many identities are waiting on it. Everything element-related
// - injection, the `load`/`error` listeners, the deadline - lives here, so
// replacing a stalled element can never strand a caller listening to the node
// that was removed.
let _frigadeScriptReady: Promise<boolean> | null = null;
// Tracks whether the vendor bundle has taken over the global. While it is
// false, `window.FrigadeAssistant` is still our stub and `.q` is our own array.
let _frigadeScriptLoaded = false;

function loadFrigadeScript(): Promise<boolean> {
  if (_frigadeScriptReady) {
    return _frigadeScriptReady;
  }
  const promise = new Promise<boolean>((resolve) => {
    const script = installFrigadeBootstrap();
    let settled = false;
    let timeoutId: number | undefined;
    const settle = (ok: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      // Cancel the timer so it can't mutate the element after the fact -
      // stamping the timed-out marker on a script that has since loaded, or on
      // one the `error` path already removed.
      window.clearTimeout(timeoutId);
      if (!ok) {
        // Failures are retryable: the next call re-attempts the bootstrap.
        _frigadeScriptReady = null;
      }
      resolve(ok);
    };
    if (script.dataset.frigadeReady === `true`) {
      settle(true);
      return;
    }
    script.addEventListener(`load`, () => {
      script.dataset.frigadeReady = `true`;
      _frigadeScriptLoaded = true;
      // A download that lands after the timeout is still good: clear the
      // marker so the next call reuses this element instead of re-injecting.
      delete script.dataset.frigadeTimedOut;
      settle(true);
    });
    script.addEventListener(`error`, () => {
      // Yank the dead element before settling. It will never fire another
      // event, so leaving it in place would make the next call hit the `src`
      // dedupe in `installFrigadeBootstrap()`, attach listeners to a corpse and
      // wait out a fresh timeout instead of re-downloading.
      script.remove();
      settle(false);
    });
    // The timeout deliberately leaves the element in place: unlike `error`, it
    // says nothing about whether the download failed, and removing a
    // slow-but-live script cancels it. Marking it instead bounds the damage -
    // the element's `load` listener still resolves a later caller if the
    // download lands, and if a caller asks again first,
    // `installFrigadeBootstrap()` trades the stalled element for a fresh one.
    timeoutId = window.setTimeout(() => {
      script.dataset.frigadeTimedOut = `true`;
      settle(false);
    }, 10_000);
  });
  _frigadeScriptReady = promise;
  return promise;
}

// The vendor attaches `show`/`hide`/`close`/`getMode`/`ask` from a React effect
// that runs after its bundle's `load` event, so resolving on `load` alone hands
// callers an API whose methods are still undefined - and every call site uses
// optional chaining, so they would silently no-op.
//
// Every config call after the first re-initializes: the vendor unmounts its
// React root and mounts a new one, whose effects overwrite the methods with
// fresh closures. The effects have no cleanup, so between the config call and
// the new mount the OLD closures stay attached and dispatch into a dead tree.
// `previousShow` is the `show` the root this config replaces had installed; the
// API is ready only once something else is attached in its place.
//
// Known limitation: a mount is identified by closure identity, so two roots
// that attach within one poll interval collapse into a single observation. The
// earlier wait sees only the later root's `show` and reports it as its own,
// which leaves the later wait watching a closure nothing will replace - it
// times out and tells its caller the assistant never mounted, though it is live
// on screen. Reaching it takes two same-identity sends close enough that both
// re-initializations land inside FRIGADE_API_POLL_MS, which in turn needs the
// script already loaded: while it is still downloading `sendFrigadeConfig`
// drops the earlier queued config instead, so only one root is ever mounted.
//
// Known limitation, the mirror case: closure identity also cannot tell a root
// that mounts late from the next one. A wait that gives up at
// FRIGADE_API_READY_TIMEOUT_MS reports the closure it started from, so the next
// send for that identity waits past that same baseline - the closure from
// before the timed-out config was sent - and the timed-out config's own root,
// attaching afterwards, satisfies it. (Where the timed-out wait had already
// settled and dropped the identity's entry, the next send is not chained to it
// at all and snapshots `show` itself, which any later root likewise satisfies.)
// The caller is told the assistant is ready while the root on screen is still
// the predecessor's, so the footer shows it against the PREVIOUS config's
// `userProperties` - the project, permissions and plan the user just left. The
// newest config's own re-initialization then replaces that root, and with
// `defaultVisible: false` it comes back hidden. Reaching it needs a mount that
// takes longer than the ten seconds below, i.e. a vendor that is already badly
// degraded.
const FRIGADE_API_READY_TIMEOUT_MS = 10_000;
const FRIGADE_API_POLL_MS = 50;

function whenImperativeApiReady(previousShow: FrigadeShowFn | undefined): Promise<FrigadeMountResult> {
  const attachedShow = () => {
    const show = window.FrigadeAssistant?.show;
    return show && show !== previousShow ? show : undefined;
  };
  const alreadyAttached = attachedShow();
  if (alreadyAttached) {
    return Promise.resolve({ready: true, show: alreadyAttached});
  }
  return new Promise((resolve) => {
    const deadline = Date.now() + FRIGADE_API_READY_TIMEOUT_MS;
    const timer = window.setInterval(() => {
      const show = attachedShow();
      if (show) {
        window.clearInterval(timer);
        resolve({ready: true, show});
      } else if (Date.now() >= deadline) {
        window.clearInterval(timer);
        resolve({ready: false, show: previousShow});
      }
    }, FRIGADE_API_POLL_MS);
  });
}

// Sends the config, and returns whether it displaced an earlier config for the
// same identity that the vendor had not read yet.
//
// While the script is still downloading, `.q` is our own stub's array: dropping
// the identity's earlier entries means the vendor initializes once, from the
// newest properties, instead of mounting a root for a config nobody wants
// anymore. That matters for the caller waiting on that earlier config too - the
// mount it gets is this call's, which is newer than what it asked for, so both
// callers can share it. Once the vendor owns the global there is no queue to
// edit: each call re-initializes on its own and every caller has to wait for
// its own mount.
function sendFrigadeConfig(config: FrigadeAssistantConfig): boolean {
  const assistant = window.FrigadeAssistant;
  if (!assistant) {
    return false;
  }
  assistant(config);
  const queue = assistant.q;
  if (_frigadeScriptLoaded || !queue) {
    return false;
  }
  let displaced = false;
  // Skip the last entry: that is the config just pushed above.
  for (let index = queue.length - 2; index >= 0; index--) {
    const queued = queue[index];
    if (
      queued.apiKey === config.apiKey &&
      queued.assistantId === config.assistantId &&
      queued.userId === config.userId
    ) {
      queue.splice(index, 1);
      displaced = true;
    }
  }
  return displaced;
}

export function loadFrigadeAssistant(options: LoadFrigadeAssistantOptions): Promise<boolean> {
  const {publicKey, assistantId, userId, userProperties, preferences, tools} = options;
  // Empty credentials mean the backend couldn't resolve this environment's
  // Frigade key or assistant yet - skip the bootstrap so the page degrades to
  // "no assistant" instead of loading an unverified widget.
  if (isTestEnv() || !publicKey || !assistantId || !userId) {
    return Promise.resolve(false);
  }
  const cacheKey = identityCacheKey(publicKey, assistantId, userId);
  // Snapshot before sending the config: if the script is already up, this is
  // the closure the re-initialization will replace.
  const previousShow = window.FrigadeAssistant?.show;
  // Start the script first: it installs the `window.FrigadeAssistant` stub, so
  // the config below queues on `.q` in call order.
  const scriptReady = loadFrigadeScript();
  // The config is sent on every call, not just the first per identity. The
  // vendor exposes no method for updating a user after init: re-invoking the
  // global is its contract for refreshed `userProperties` (it re-initializes
  // in place, and a conversation under five minutes old survives via
  // sessionStorage).
  // `defaultVisible: false` because the Help menu drives visibility - the
  // assistant must never paint its own launcher button.
  const displacedQueuedConfig = sendFrigadeConfig({
    apiKey: publicKey,
    apiUrl: FRIGADE_API_URL,
    assistantId,
    userId,
    userProperties,
    preferences,
    tools,
    defaultVisible: false,
  });
  const previousLoad = _frigadeLoads.get(cacheKey);
  if (previousLoad && displacedQueuedConfig) {
    // The vendor never saw the config this one displaced, so there is a single
    // mount coming and it carries these properties. Both callers are waiting
    // for the same thing.
    return previousLoad.ready;
  }
  // Wait for the mount of the config THIS call sent, not for whichever root
  // happens to attach first. The vendor applies an identity's configs in the
  // order they were sent, so the waits chain in that order: this one starts
  // from the `show` the previous send settled on, the only closure this send's
  // root is guaranteed to replace. Comparing against whatever `show` happens to
  // be when this call runs would be wrong for a config queued before the script
  // loaded, where it is still undefined for every send.
  // A predecessor that ended not-ready settles this call too only when the
  // script never loaded: with no bundle there is no root coming for anyone.
  // Once it is up, this call's own config was still sent and re-initializes, so
  // a predecessor that merely timed out must not decide this call's result.
  const mounted: Promise<FrigadeMountResult> = previousLoad
    ? previousLoad.mounted.then((previous) =>
        previous.ready || _frigadeScriptLoaded ? whenImperativeApiReady(previous.show) : previous,
      )
    : scriptReady.then((ok) =>
        // Failures are retryable: the next call for this identity re-sends the
        // config against a fresh script attempt.
        ok ? whenImperativeApiReady(previousShow) : {ready: false, show: previousShow},
      );
  const ready = mounted.then((result) => {
    // Dropping the entry lets the next call for this identity re-attempt the
    // script and start a fresh wait chain. Only the newest send owns the entry:
    // a later one may have replaced it while this wait was running.
    if (!result.ready && _frigadeLoads.get(cacheKey)?.mounted === mounted) {
      _frigadeLoads.delete(cacheKey);
    }
    return result.ready;
  });
  _frigadeLoads.set(cacheKey, {mounted, ready});
  return ready;
}
